import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, TestTube, UsersThree, ClipboardText, ArrowRight, Warning } from "@phosphor-icons/react";

const MODES = {
  scope: {
    title: "Project Scope", icon: ClipboardText, anchor: "rd-scope",
    blurb: "Internal project scope is planned per saved formula workspace. Pick a saved formula to view or edit its scope. This is planning only, not a signed agreement.",
  },
  tests: {
    title: "Analytical Tests", icon: TestTube, anchor: "rd-tests",
    blurb: "Lab results are recorded inside a saved formula so each record stays tied to the exact revision tested. Pick a saved formula to view or add analytical records.",
  },
  sensory: {
    title: "Sensory Evaluations", icon: UsersThree, anchor: "rd-studies",
    blurb: "Sensory studies are run against a specific saved revision. Pick a saved formula to view studies or enter internal panel responses.",
  },
};

export default function ResearchRecords() {
  const loc = useLocation();
  const mode = loc.pathname.includes("sensory") ? MODES.sensory : loc.pathname.includes("scope") ? MODES.scope : MODES.tests;
  const Icon = mode.icon;
  const [recipes, setRecipes] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setError(""); setRecipes(null);
    try { const { data } = await api.get("/recipes"); setRecipes(Array.isArray(data) ? data : []); }
    catch (err) { setError(formatApiError(err?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (recipes || [])
      .filter((r) => !s || `${r.name || ""} ${r.product_name || ""} ${r.sku || ""}`.toLowerCase().includes(s))
      .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }, [recipes, q]);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={`page-research-${mode.anchor}`}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500"><Icon size={14} /> Research records</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{mode.title}</h1>
        <p className="text-sm text-stone-600 mt-2 max-w-2xl">{mode.blurb}</p>

        <label className="block mt-6">
          <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Pick a saved formula</span>
          <div className="relative mt-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, product name or SKU"
              className="w-full rounded-md border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#17324D]/30"
              data-testid="input-formula-search" />
          </div>
        </label>

        <div className="mt-4">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-center gap-2" data-testid="error-recipes">
              <Warning size={16} /> <span className="flex-1">Could not load formulas. {error}</span>
              <Button size="sm" variant="outline" onClick={load} data-testid="button-retry">Retry</Button>
            </div>
          ) : recipes === null ? (
            <div className="space-y-2 animate-pulse">{[0, 1, 2].map((i) => <div key={i} className="h-14 bg-stone-100 rounded-md" />)}</div>
          ) : recipes.length === 0 ? (
            <div className="rounded-md border border-dashed border-stone-300 bg-white px-4 py-8 text-center text-sm text-stone-500">
              No saved formulas yet. <Link to="/builder" className="underline text-[#17324D]">Create a formula</Link> and save it to start recording evidence.
            </div>
          ) : list.length === 0 ? (
            <p className="text-sm text-stone-500 py-4">No formulas match "{q}".</p>
          ) : (
            <div className="divide-y divide-stone-100 rounded-md border border-stone-200 bg-white">
              {list.map((r) => (
                <Link key={r.id} to={`/rd/${r.id}#${mode.anchor}`} data-testid={`link-formula-${r.id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#EAF0F6]">
                  <span className="min-w-0">
                    <span className="block font-medium text-stone-800 truncate">{r.product_name || r.name}</span>
                    <span className="block text-xs text-stone-500">
                      {r.sku && <span className="font-mono mr-2">{r.sku}</span>}
                      {r.version || "v1"} · {r.status || "R&D"}{r.updated_at ? ` · updated ${new Date(r.updated_at).toLocaleDateString()}` : ""}
                    </span>
                  </span>
                  <ArrowRight size={16} className="text-stone-400 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
